import React, { useState, useEffect } from "react"
import {
  X, Package, Calendar, Hash, Check, XCircle, Loader, AlertTriangle, FileText,
} from "lucide-react"
import { useTheme } from "../contexts/ThemeContext"

const API_URL = process.env.REACT_APP_API_URL

/**
 * Modal de aceptación/edición/denegación de un albarán PENDIENTE (modo 'mixto').
 *
 * Lo abre el cliente desde la pestaña "Albaranes pendientes" de GestionStock.
 * Carga las líneas vía GET /api/pedidos/:pedidoId/albaran-pendiente, permite
 * editar `cantidad_aceptada` por línea, y dispara aceptar o denegar.
 *
 * Al aceptar:
 *   - Backend crea stock_movimientos + lotes + actualiza stock.
 *   - Si todo lo aceptado cubre el pedido → pedido pasa a estado 3 (Enviado).
 *   - Si quedó alguna línea corta → estado 4 (Envío parcial).
 *
 * Props:
 *   - open
 *   - pedidoId      ← obligatorio
 *   - onClose()
 *   - onResolved()  ← tras aceptar o denegar, para refrescar lista
 */
export default function AlbaranMixtoAceptarModal({ open, pedidoId, onClose, onResolved }) {
  const { theme: themeCtx } = useTheme()
  const theme = {
    primary:      themeCtx?.primary      || "#3cbf9a",
    primaryDark:  themeCtx?.primaryDark  || "#2da080",
    primaryLight: themeCtx?.primaryLight || "rgba(60, 191, 154, 0.10)",
    danger:       "#dc2626",
    dangerLight:  "rgba(220, 38, 38, 0.08)",
  }

  const [albaran, setAlbaran] = useState(null)
  const [lineas, setLineas] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [working, setWorking] = useState(false)
  const [confirmDeny, setConfirmDeny] = useState(false)
  const [motivoDeny, setMotivoDeny] = useState("")

  useEffect(() => {
    if (!open || !pedidoId) return
    let cancelled = false
    async function cargar() {
      setLoading(true); setError(""); setConfirmDeny(false); setMotivoDeny("")
      try {
        const token = localStorage.getItem("token")
        const r = await fetch(`${API_URL}/api/pedidos/${pedidoId}/albaran-pendiente`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const data = await r.json()
        if (!r.ok) throw new Error(data.message || "Error al cargar albarán")
        if (cancelled) return
        setAlbaran(data.albaran)
        // Inicializamos cantidad_aceptada con el valor del backend (si ya se editó)
        // o con cantidad_unidades (lo que dijo el albarán original).
        setLineas((data.lineas || []).map(l => ({
          ...l,
          cantidad_aceptada_edit: l.cantidad_aceptada != null
            ? l.cantidad_aceptada
            : l.cantidad_unidades,
          lote_edit: l.lote || "",
          fecha_edit: l.fecha_vencimiento ? l.fecha_vencimiento.slice(0, 10) : "",
        })))
      } catch (e) {
        if (!cancelled) setError(e.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    cargar()
    return () => { cancelled = true }
  }, [open, pedidoId])

  if (!open) return null

  const handleLineaChange = (idx, field, value) => {
    setLineas(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l))
  }

  const handleAceptar = async () => {
    if (!albaran) return
    setError("")

    // Validar lote obligatorio en CADA línea que vaya a sumar stock (cantidad > 0).
    // Si vino vacío del albarán original, el cliente DEBE rellenarlo aquí antes
    // de aceptar, para que el lote quede trazado en stock_lotes.
    const conflictiva = lineas.find(l =>
      (parseInt(l.cantidad_aceptada_edit) || 0) > 0 && (!l.lote_edit || !l.lote_edit.trim())
    )
    if (conflictiva) {
      setError(`La línea de "${conflictiva.nombre}" necesita un lote antes de aceptar (obligatorio en modo mixto).`)
      return
    }

    setWorking(true)
    try {
      const token = localStorage.getItem("token")
      const body = {
        lineas: lineas.map(l => ({
          id: l.id,
          cantidad_aceptada: parseInt(l.cantidad_aceptada_edit) || 0,
          lote: l.lote_edit ? l.lote_edit.trim() : null,
          fecha_vencimiento: l.fecha_edit || null,
        })),
      }
      const r = await fetch(`${API_URL}/api/albaranes-pendientes/${albaran.id}/aceptar`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.message || "Error al aceptar")
      onResolved?.(data)
      onClose?.()
    } catch (e) {
      setError(e.message)
    } finally {
      setWorking(false)
    }
  }

  const handleDenegar = async () => {
    if (!albaran) return
    setWorking(true); setError("")
    try {
      const token = localStorage.getItem("token")
      const r = await fetch(`${API_URL}/api/albaranes-pendientes/${albaran.id}/denegar`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ motivo: motivoDeny || null }),
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.message || "Error al denegar")
      onResolved?.(data)
      onClose?.()
    } catch (e) {
      setError(e.message)
    } finally {
      setWorking(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ backgroundColor: theme.primaryLight }}>
          <div>
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <FileText size={18} style={{ color: theme.primaryDark }} />
              Verificar albarán pendiente
            </h2>
            {albaran && (
              <p className="text-xs text-gray-600 mt-1">
                Albarán <strong>{albaran.numero}</strong> · Pedido #{albaran.pedido_id} ·
                Cargado el {new Date(albaran.created_at).toLocaleDateString("es-ES")}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/50" disabled={working}>
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader size={20} className="animate-spin mr-2" /> Cargando albarán...
            </div>
          ) : !albaran ? (
            <div className="text-center py-12 text-gray-500">{error || "Sin datos"}</div>
          ) : (
            <>
              {albaran.notas && (
                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 text-blue-800 text-xs rounded">
                  <strong>Notas del emisor:</strong> {albaran.notas}
                </div>
              )}

              <p className="text-xs text-gray-600 mb-3">
                Revisa cada línea. Si recibiste exactamente lo que dice el albarán, no toques nada.
                Si recibiste menos (o cero), edita la columna <strong>Cantidad aceptada</strong>.
              </p>

              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2">Producto</th>
                    <th className="text-left px-3 py-2">Unidad base</th>
                    <th className="text-right px-3 py-2">Cant. en albarán</th>
                    <th className="text-right px-3 py-2">Cantidad aceptada</th>
                    <th className="text-left px-3 py-2">Lote <span className="text-red-500">*</span></th>
                    <th className="text-left px-3 py-2">Vencimiento</th>
                  </tr>
                </thead>
                <tbody>
                  {lineas.map((l, idx) => {
                    const diff = (parseInt(l.cantidad_aceptada_edit) || 0) !== (parseInt(l.cantidad_unidades) || 0)
                    return (
                      <tr key={l.id} className={`border-t hover:bg-gray-50 ${diff ? "bg-yellow-50" : ""}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-800">{l.nombre}</div>
                          {l.ref_proveedor && <div className="text-[10px] text-gray-500">{l.ref_proveedor}</div>}
                        </td>
                        <td className="px-3 py-2 text-gray-500 text-[11px]">{l.unidadbasica || "—"}</td>
                        <td className="px-3 py-2 text-right">
                          <span className="font-semibold text-gray-700">{l.cantidad_unidades}</span>
                          <div className="text-[10px] text-gray-400">unidades</div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number" min="0"
                            value={l.cantidad_aceptada_edit}
                            onChange={(e) => handleLineaChange(idx, "cantidad_aceptada_edit", e.target.value)}
                            className="w-24 px-2 py-1 text-right border-2 rounded font-semibold focus:outline-none"
                            style={{ borderColor: diff ? "#f59e0b" : "#e5e7eb", color: diff ? "#92400e" : "inherit" }}
                          />
                          {diff && (
                            <div className="text-[10px] text-amber-700 mt-0.5">
                              {parseInt(l.cantidad_aceptada_edit) > parseInt(l.cantidad_unidades) ? "más" : "menos"} de lo que dice
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <Hash size={11} className="text-gray-400" />
                            <input
                              type="text"
                              value={l.lote_edit}
                              onChange={(e) => handleLineaChange(idx, "lote_edit", e.target.value.toUpperCase())}
                              required
                              placeholder="Obligatorio"
                              className="w-28 px-2 py-1 border-2 rounded text-[11px] uppercase focus:outline-none"
                              style={{ borderColor: l.lote_edit && l.lote_edit.trim() ? "#e5e7eb" : "#fca5a5" }}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <Calendar size={11} className="text-gray-400" />
                            <input
                              type="date"
                              value={l.fecha_edit}
                              onChange={(e) => handleLineaChange(idx, "fecha_edit", e.target.value)}
                              className="w-32 px-2 py-1 border-2 border-gray-200 rounded text-[11px] focus:outline-none"
                            />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>

              {error && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {confirmDeny && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-800 font-semibold mb-2">
                    ¿Seguro que quieres denegar este albarán?
                  </p>
                  <p className="text-xs text-red-700 mb-2">
                    El stock no se actualizará. El emisor podrá crear un albarán corregido después.
                  </p>
                  <input
                    type="text"
                    value={motivoDeny}
                    onChange={(e) => setMotivoDeny(e.target.value)}
                    placeholder="Motivo (opcional)"
                    className="w-full px-2 py-1 border-2 border-red-200 rounded text-sm focus:outline-none focus:border-red-400"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-gray-50 flex justify-between gap-2">
          {!confirmDeny ? (
            <>
              <button
                onClick={() => setConfirmDeny(true)}
                disabled={working || loading}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded flex items-center gap-1 disabled:opacity-50"
              >
                <XCircle size={14} /> Denegar
              </button>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  disabled={working}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAceptar}
                  disabled={working || loading || lineas.length === 0}
                  className="px-4 py-2 text-sm font-semibold text-white rounded flex items-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: theme.primary }}
                >
                  {working ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
                  Aceptar y aplicar al stock
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={() => { setConfirmDeny(false); setMotivoDeny("") }}
                disabled={working}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded disabled:opacity-50"
              >
                Volver
              </button>
              <button
                onClick={handleDenegar}
                disabled={working}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded flex items-center gap-2 disabled:opacity-50"
              >
                {working ? <Loader size={14} className="animate-spin" /> : <XCircle size={14} />}
                Confirmar denegación
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
